// `extension-criteria-ats-filters-data-helpers.ng1.ts` — the five per-type handlers, as data.
//
// Upstream registers one class per field type, each with `getNewData`, `isValid`,
// `getTemplateUrl` and `shouldHandle`, and picks between them by filtering the list. Here the
// template choice is a component and the other three are functions over a discriminated union,
// which is the same behaviour with the dispatch spelled out.
//
// ⚠️ EACH TYPE'S DEFAULT CONDITION IS NOT THE SAME. Text and multi-select start at INCLUDE_ANY;
// SINGLE-SELECT STARTS AT INCLUDE_ALL, which its own select renders as "Include" — a different
// word for a different enum member in a two-option list. Copying one default across all three,
// which their otherwise identical shape invites, changes what a new single-select filter means.
import { AtsFilterConditionType, AtsFiltersFieldType, type AtsFilterValue } from '@woo-ext/api'

export interface AtsFilterTextData {
  type: typeof AtsFiltersFieldType.Text
  values: string[]
  condition: AtsFilterConditionType
}

export interface AtsFilterNumericData {
  type: typeof AtsFiltersFieldType.Numeric
  min: number | null
  max: number | null
}

export interface AtsFilterDateData {
  type: typeof AtsFiltersFieldType.Date
  from: Date | null
  to: Date | null
}

export interface AtsFilterMultiselectData {
  type: typeof AtsFiltersFieldType.MultiSelect
  values: AtsFilterValue[]
  condition: AtsFilterConditionType
}

export interface AtsFilterSingleselectData {
  type: typeof AtsFiltersFieldType.SingleSelect
  value: AtsFilterValue | null
  condition: AtsFilterConditionType
}

export type AtsFilterData =
  AtsFilterTextData | AtsFilterNumericData | AtsFilterDateData | AtsFilterMultiselectData | AtsFilterSingleselectData

/** `ITempExtensionCriteriaAtsFilter` — a row of the table, both halves optional while editing. */
export interface AtsFilterRow {
  section?: string
  data?: AtsFilterData
}

/**
 * `getNewDataByType` — what a row becomes the moment its Field select changes.
 *
 * ⚠️ THE OLD DATA IS DISCARDED, NOT MERGED. `onAtsFiltersSectionChange` assigns a fresh object,
 * so switching a row from a date field to a numeric one drops the dates rather than carrying a
 * `from` that the numeric inputs cannot show and the server would still receive.
 *
 * ⚠️ AND `DATE` DEFAULTS `to` TO TODAY, not to null — the only type whose blank state is not
 * empty. Its own `isValid` requires `to`, so a fresh date filter is valid on creation while a
 * fresh numeric one is not.
 */
export function newAtsFilterData(type: AtsFiltersFieldType, now: Date = new Date()): AtsFilterData {
  switch (type) {
    case AtsFiltersFieldType.Text:
      return { type: AtsFiltersFieldType.Text, values: [], condition: AtsFilterConditionType.IncludeAny }
    case AtsFiltersFieldType.Numeric:
      return { type: AtsFiltersFieldType.Numeric, min: null, max: null }
    case AtsFiltersFieldType.Date:
      return { type: AtsFiltersFieldType.Date, from: null, to: now }
    case AtsFiltersFieldType.MultiSelect:
      return { type: AtsFiltersFieldType.MultiSelect, values: [], condition: AtsFilterConditionType.IncludeAny }
    case AtsFiltersFieldType.SingleSelect:
      return { type: AtsFiltersFieldType.SingleSelect, value: null, condition: AtsFilterConditionType.IncludeAll }
  }
}

/** `isDataValid` — what the form's `validAtsFilters` error is computed from. */
export function isAtsFilterDataValid(data: AtsFilterData | undefined): boolean {
  if (data === undefined) return false
  switch (data.type) {
    case AtsFiltersFieldType.Text:
    case AtsFiltersFieldType.MultiSelect:
      return data.values.length > 0
    case AtsFiltersFieldType.Numeric:
      return data.min !== null && data.max !== null
    // ⚠️ `to > from`, STRICTLY, and a null `from` passes. Reading it as a range check with
    // `>=` accepts a zero-length range production rejects.
    case AtsFiltersFieldType.Date:
      return data.to !== null && (data.from === null || data.to > data.from)
    case AtsFiltersFieldType.SingleSelect:
      return data.value !== null
  }
}

/**
 * `validateAtsFiltersCriteria` — the FORM-level error the per-row messages hang off.
 *
 * ⚠️ WITHOUT IT THE MESSAGES ARE DEAD MARKUP, which is how they shipped: every label and
 * every red border in this section is gated on `ctrl.form.atsFilters.$error.validAtsFilters`,
 * and nothing in this port ever computed it — so picking an ATS field and leaving its value
 * empty said nothing, where production shows "Heads up! This field needs your input" under
 * the row. Measured side by side on the contact builder with Categories picked and no value.
 *
 * ⚠️ AND A SINGLE EMPTY ROW IS VALID. `isEmptyOnLoad` — one filter, `{}` — is how the section
 * renders before the user has touched it, and flagging that would put an error on a form
 * nobody has filled in yet.
 */
export function atsFiltersHaveErrors(rows: readonly AtsFilterRow[]): boolean {
  const isEmptyOnLoad =
    rows.length === 1 && rows[0]?.section === undefined && rows[0]?.data === undefined
  if (isEmptyOnLoad) return false
  return rows.some(
    (row) => row.section === undefined || row.section === '' || !isAtsFilterDataValid(row.data),
  )
}

/** `atsFiltersTableHeadlinesByType` — the column labels, which differ per type. */
export const ATS_FILTER_HEADLINES: Record<AtsFiltersFieldType, readonly string[]> = {
  [AtsFiltersFieldType.Text]: ['Condition'],
  [AtsFiltersFieldType.MultiSelect]: ['Condition'],
  [AtsFiltersFieldType.SingleSelect]: ['Condition'],
  [AtsFiltersFieldType.Numeric]: ['From', 'To'],
  [AtsFiltersFieldType.Date]: ['From', 'To'],
}

export interface ConditionOption {
  display: string
  value: AtsFilterConditionType
}

/** `atsFiltersConditionDisplayText` — three, for text and multi-select. */
export const ATS_FILTER_CONDITIONS: readonly ConditionOption[] = [
  { display: 'Include all', value: AtsFilterConditionType.IncludeAll },
  { display: 'Include any', value: AtsFilterConditionType.IncludeAny },
  { display: 'Exclude', value: AtsFilterConditionType.Exclude },
]

/**
 * `atsFiltersSingleselectConditionDisplayText` — TWO, and the first reads "Include" while
 * carrying `INCLUDE_ALL`. Not a subset of the list above: the wording changes too.
 */
export const ATS_FILTER_SINGLESELECT_CONDITIONS: readonly ConditionOption[] = [
  { display: 'Include', value: AtsFilterConditionType.IncludeAll },
  { display: 'Exclude', value: AtsFilterConditionType.Exclude },
]

/** The field type a section maps to, from the tenant's configuration. */
export function atsFilterTypeForSection(
  fields: ReadonlyArray<{ section: string; type: AtsFiltersFieldType }>,
  section: string | undefined,
): AtsFiltersFieldType | undefined {
  if (section === undefined || section === '') return undefined
  return fields.find((field) => field.section === section)?.type
}
